import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Users, Clock, Shield } from 'lucide-react';
import SEO from '../components/SEO';
import { FadeInUp } from '../components/Animations';

const HomePage = () => {
  const services = [
    {
      title: 'ASD Assessment',
      description: 'Comprehensive autism spectrum assessments for women, men, and children via telehealth.',
      link: '/asd-assessment',
      icon: '',
      keywords: ['Women', 'Men', 'Children', 'Online', 'Affordable'],
      image: '/images/asd-puzzle.png'
    },
    {
      title: 'ADHD Assessment',
      description: 'Professional ADHD assessments tailored to your unique needs, fully conducted online.',
      link: '/adhd-assessment',
      icon: '',
      keywords: ['Adults', 'Children', 'Telehealth', 'Comprehensive'],
      image: '/images/adhd-runner.png'
    },
    {
      title: 'Therapy Services',
      description: 'Evidence-based telehealth therapy for individuals and families across Australia.',
      link: '/therapy',
      icon: '',
      keywords: ['Anxiety', 'Depression', 'Trauma', 'Online'],
      image: 'https://images.unsplash.com/photo-1588873281272-14886ba1f737?auto=format&fit=crop&w=600&q=80'
    }
  ];

  const features = [
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Fully Online',
      description: 'Accessible telehealth services from anywhere in Australia'
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Flexible Appointments',
      description: 'Book times that work with your schedule'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Professional & Confidential',
      description: 'Registered psychologists with years of experience'
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: 'Evidence-Based',
      description: 'Using proven therapeutic approaches and assessments'
    }
  ];

  return (
    <>
      <SEO
        title="Telehealth Psychology Services - ASD & ADHD Assessments"
        description="Professional telehealth psychology services specialising in ASD and ADHD assessments, and evidence-based therapy. Serving clients across Australia online."
        keywords="telehealth psychology, ASD assessment, ADHD assessment, online therapy, autism assessment, ADHD diagnosis, psychology services Australia"
        url="/"
      />

      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-white z-10"></div>
          <img 
            src="https://images.unsplash.com/photo-1612831455359-970e23a1e4e9?auto=format&fit=crop&w=1920&q=80" 
            alt="Professional telehealth psychology services"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="container mx-auto px-4 relative z-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                Compassionate Psychology Services,
                <span style={{ color: 'var(--blue-primary)' }}> Fully Online</span>
              </h1>
              <p className="text-lg md:text-xl mb-8" style={{ color: 'var(--text-secondary)' }}>
                Specialising in ASD and ADHD assessments, plus evidence-based therapy for individuals and families across Australia.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-primary"
                  className="px-8 py-4 bg-[var(--blue-primary)] text-white rounded-full font-medium hover:bg-[var(--button-primary-hover)] transition-all duration-300 hover:scale-105 hover:shadow-lg inline-flex items-center justify-center"
                >
                  Book an Assessment
                  <ArrowRight className="ml-2" size={20} />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-cta-secondary"
                  className="px-8 py-4 bg-white text-[var(--blue-primary)] border-2 border-[var(--blue-primary)] rounded-full font-medium hover:bg-[var(--blue-pale)] transition-all duration-300 inline-flex items-center justify-center"
                >
                  Learn More
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:block"
            >
              <img 
                src="/images/hero-background.png" 
                alt="Professional online therapy session"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                Our Services
              </h2>
              <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
                Comprehensive psychology services delivered through secure telehealth technology
              </p>
            </div>
          </FadeInUp>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <FadeInUp key={service.title} delay={index * 0.1}>
                <Link to={service.link} data-testid={`service-card-${service.title.toLowerCase().replace(' ', '-')}`}>
                  <motion.div
                    whileHover={{ y: -8, boxShadow: '0 12px 24px rgba(0,0,0,0.1)' }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-2xl shadow-md h-full flex flex-col overflow-hidden"
                  >
                    <div className="h-48 overflow-hidden">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-8 flex flex-col flex-grow">
                      {service.icon && <div className="text-4xl mb-4">{service.icon}</div>}
                      <h3 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                        {service.title}
                      </h3>
                      <p className="mb-4 flex-grow" style={{ color: 'var(--text-secondary)' }}>
                        {service.description}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {service.keywords.map((keyword) => (
                          <span
                            key={keyword}
                            className="px-3 py-1 bg-[var(--blue-pale)] text-[var(--navy-primary)] text-sm rounded-full"
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center text-[var(--blue-primary)] font-medium">
                        Learn more <ArrowRight size={16} className="ml-2" />
                      </div>
                    </div>
                  </motion.div>
                </Link>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section gray-bg">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                Why Choose Navigating Minds?
              </h2>
              <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
                Professional, accessible, and compassionate care when you need it most
              </p>
            </div>
          </FadeInUp>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <FadeInUp key={feature.title} delay={index * 0.1}>
                <div className="text-center bg-white p-6 rounded-xl">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--blue-pale)] rounded-full mb-4" style={{ color: 'var(--blue-primary)' }}>
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                    {feature.title}
                  </h3>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    {feature.description}
                  </p>
                </div>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="bg-[var(--blue-primary)] text-white rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute inset-0 opacity-10">
                <img 
                  src="https://images.pexels.com/photos/3958396/pexels-photo-3958396.jpeg?auto=compress&cs=tinysrgb&w=1200" 
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Ready to Take the First Step?
                </h2>
                <p className="text-lg mb-8 max-w-2xl mx-auto opacity-90">
                  Book an assessment or therapy session today. We're here to support you on your journey.
                </p>
                <Link
                  to="/contact"
                  data-testid="cta-bottom-button"
                  className="px-8 py-4 bg-white text-[var(--blue-primary)] rounded-full font-medium hover:bg-[var(--gray-100)] transition-all duration-300 hover:scale-105 inline-flex items-center"
                >
                  Contact Us Today
                  <ArrowRight className="ml-2" size={20} />
                </Link>
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>
    </>
  );
};

export default HomePage;